import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '@shared/lib/supabase';
import type {
  UserSkillWithMaster,
  SkillsDashboardSummary,
  SkillsFilterOptions,
  SkillsSortOptions
} from '@shared/types/skills-tracker.types';

interface UserReport {
  id: string;
  title: string;
  created_at: string;
  status: string;
}

interface ImportResult {
  success: boolean;
  skills_extracted: number;
  skills_added_to_tracker: number;
  milestones_created: number;
  errors: string[];
  warnings: string[];
}

interface SkillsTrackerContextType {
  skills: UserSkillWithMaster[];
  summary: SkillsDashboardSummary | null;
  loading: boolean;
  error: string | null;
  filters: SkillsFilterOptions;
  sortOptions: SkillsSortOptions;
  setFilters: (filters: SkillsFilterOptions) => void;
  setSortOptions: (options: SkillsSortOptions) => void;
  refreshSkills: () => Promise<void>;
  updateSkillProgress: (skillId: string, progress: number) => Promise<void>;
  updateSkillPriority: (skillId: string, priority: number) => Promise<void>;
  fetchUserReports: () => Promise<UserReport[]>;
  importSkillsFromReport: (reportId: string) => Promise<ImportResult>;
}

const SkillsTrackerContext = createContext<SkillsTrackerContextType | undefined>(undefined);

export const useSkillsTracker = () => {
  const context = useContext(SkillsTrackerContext);
  if (!context) {
    throw new Error('useSkillsTracker must be used within SkillsTrackerProvider');
  }
  return context;
};

interface SkillsTrackerProviderProps {
  children: React.ReactNode;
}

export const SkillsTrackerProvider: React.FC<SkillsTrackerProviderProps> = ({ children }) => {
  const [skills, setSkills] = useState<UserSkillWithMaster[]>([]);
  const [summary, setSummary] = useState<SkillsDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SkillsFilterOptions>({});
  const [sortOptions, setSortOptions] = useState<SkillsSortOptions>({
    field: 'priority',
    direction: 'asc'
  });

  const fetchSkills = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Not authenticated');
      }

      let query = supabase
        .from('user_skills_tracker')
        .select(`
          *,
          skill:skills_master (
            id,
            skill_name,
            category,
            subcategory,
            description,
            market_demand_score,
            industry_tags
          )
        `)
        .eq('user_id', user.id)
        .eq('is_deleted', false);

      if (filters.status) {
        query = query.eq('status', filters.status);
      }

      if (filters.priority) {
        query = query.eq('priority', filters.priority);
      }

      if (filters.category) {
        query = query.eq('skill.category', filters.category);
      }

      if (filters.min_progress !== undefined) {
        query = query.gte('progress_percentage', filters.min_progress);
      }

      if (filters.max_progress !== undefined) {
        query = query.lte('progress_percentage', filters.max_progress);
      }

      query = query.order(sortOptions.field, { ascending: sortOptions.direction === 'asc' });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setSkills(data as UserSkillWithMaster[]);

      const stats: SkillsDashboardSummary = {
        total_skills: data?.length || 0,
        active_skills: data?.filter(s => s.status === 'active').length || 0,
        completed_skills: data?.filter(s => s.status === 'completed').length || 0,
        paused_skills: data?.filter(s => s.status === 'paused').length || 0,
        average_progress: data && data.length > 0
          ? Math.round(data.reduce((sum, s) => sum + s.progress_percentage, 0) / data.length)
          : 0,
        skills_by_priority: {
          priority_1: data?.filter(s => s.priority === 1).length || 0,
          priority_2: data?.filter(s => s.priority === 2).length || 0,
          priority_3: data?.filter(s => s.priority === 3).length || 0,
          priority_4: data?.filter(s => s.priority === 4).length || 0,
          priority_5: data?.filter(s => s.priority === 5).length || 0
        },
        skills_by_proficiency: {
          none: data?.filter(s => s.current_proficiency_level === 'none').length || 0,
          beginner: data?.filter(s => s.current_proficiency_level === 'beginner').length || 0,
          intermediate: data?.filter(s => s.current_proficiency_level === 'intermediate').length || 0,
          advanced: data?.filter(s => s.current_proficiency_level === 'advanced').length || 0,
          expert: data?.filter(s => s.current_proficiency_level === 'expert').length || 0
        },
        recent_updates_count: 0,
        estimated_completion_months_total: data?.reduce((sum, s) => sum + (s.estimated_months_to_complete || 0), 0) || 0
      };

      setSummary(stats);
    } catch (err: any) {
      console.error('Error fetching skills:', err);
      setError(err.message || 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, [filters, sortOptions]);

  const refreshSkills = useCallback(async () => {
    await fetchSkills();
  }, [fetchSkills]);

  const updateSkillProgress = useCallback(async (skillId: string, progress: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('user_skills_tracker')
        .update({
          progress_percentage: progress,
          last_progress_update: new Date().toISOString()
        })
        .eq('id', skillId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      await refreshSkills();
    } catch (err: any) {
      console.error('Error updating progress:', err);
      throw err;
    }
  }, [refreshSkills]);

  const updateSkillPriority = useCallback(async (skillId: string, priority: number) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('user_skills_tracker')
        .update({ priority })
        .eq('id', skillId)
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      await refreshSkills();
    } catch (err: any) {
      console.error('Error updating priority:', err);
      throw err;
    }
  }, [refreshSkills]);

  const fetchUserReports = useCallback(async (): Promise<UserReport[]> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: reportsError } = await supabase
        .from('reports')
        .select('id, title, created_at, status')
        .eq('user_id', user.id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(10);

      if (reportsError) throw reportsError;

      return (data || []) as UserReport[];
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      throw err;
    }
  }, []);

  const importSkillsFromReport = useCallback(async (reportId: string): Promise<ImportResult> => {
    try {
      console.log('[SkillsTracker] Starting import for report:', reportId);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[SkillsTracker] User not authenticated');
        throw new Error('Not authenticated');
      }
      console.log('[SkillsTracker] User authenticated:', user.id);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('[SkillsTracker] No active session');
        throw new Error('No active session');
      }
      console.log('[SkillsTracker] Session obtained');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      console.log('[SkillsTracker] Supabase URL:', supabaseUrl);

      const endpoint = `${supabaseUrl}/functions/v1/extract-skills-from-report`;
      console.log('[SkillsTracker] Calling endpoint:', endpoint);

      const requestBody = {
        report_id: reportId,
        config: {
          create_milestones: true,
          default_priority: 3,
          source: 'ai_generated'
        }
      };
      console.log('[SkillsTracker] Request body:', requestBody);

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      console.log('[SkillsTracker] Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorData: any;
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
          console.error('[SkillsTracker] Error response:', errorData);
        } else {
          const errorText = await response.text();
          console.error('[SkillsTracker] Error text:', errorText);
          errorData = { error: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }

        throw new Error(errorData.error || errorData.message || `Failed to import skills (${response.status})`);
      }

      const result = await response.json();
      console.log('[SkillsTracker] Import result:', result);

      await refreshSkills();
      console.log('[SkillsTracker] Skills refreshed successfully');

      return result;
    } catch (err: any) {
      console.error('[SkillsTracker] Error importing skills from report:', {
        message: err.message,
        stack: err.stack,
        name: err.name,
        cause: err.cause
      });
      throw err;
    }
  }, [refreshSkills]);

  useEffect(() => {
    fetchSkills();
  }, [fetchSkills]);

  const value: SkillsTrackerContextType = {
    skills,
    summary,
    loading,
    error,
    filters,
    sortOptions,
    setFilters,
    setSortOptions,
    refreshSkills,
    updateSkillProgress,
    updateSkillPriority,
    fetchUserReports,
    importSkillsFromReport
  };

  return (
    <SkillsTrackerContext.Provider value={value}>
      {children}
    </SkillsTrackerContext.Provider>
  );
};
